import { useState, useEffect, useRef } from "react"
import {
  IAccordionSingleProps,
  IUseAccordionSingleProps,
  IUseAccordionSingleReturn,
  IUseAccordionMultipleProps,
  IUseAccordionMultipleReturn,
} from "./types"

const useAccordionSingle = ({
  single,
  initialOpen,
  propIndex,
  open,
  onClick,
  onOpen,
  onClose,
}: IUseAccordionSingleProps): IUseAccordionSingleReturn => {
  const [isOpen, setIsOpen] = useState(open || initialOpen)
  const [index, setIndex] = useState(null)
  const initialized = useRef(false)
  const firstRender = useRef(true)

  const itemId = `ds-item-${index}`
  const panelId = `ds-panel-${index}`

  const toggleAccordion = (): void => {
    setIsOpen(!isOpen)
  }

  const handleClick = (index: number | string): void => {
    if (onClick) onClick(index)
    if (single) {
      toggleAccordion()
    }
  }

  useEffect((): void => {
    if (!initialized.current) {
      initialized.current = true
      return
    }
    if (open !== null) {
      setIsOpen(open)
    }
  }, [open])

  useEffect(() => {
    setIndex(propIndex ?? 1)
  }, [])

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    if (isOpen && onOpen) onOpen(index)
    if (!isOpen && onClose) onClose(index)
  }, [isOpen])

  return {
    isOpen,
    itemId,
    panelId,
    index,
    handleClick,
  }
}

const useAccordionMultiple = ({
  items,
  oneAtATime,
  onClick,
}: IUseAccordionMultipleProps): IUseAccordionMultipleReturn => {
  const [actives, setActives] = useState([])

  const toggleAccordion = (index: number | string): void => {
    if (actives.includes(index)) {
      const newActives = actives.filter((accordion) => accordion != index)
      setActives(newActives)
      return
    }
    if (oneAtATime) {
      setActives([index])
      return
    }
    const newActives = [...actives]
    newActives.push(index)
    setActives(newActives)
  }

  /**
   * Verifica se todos os itens possuem um index e verifica se não há indexes repetidos
   * @param items Array com os conteúdos dos acordeões
   */
  const checkIndexes = (items: Array<IAccordionSingleProps>): void => {
    const indexMap = {}
    items.forEach((item, i) => {
      if (item.index === undefined) {
        item.index = i + 1
      }
      if (!indexMap[item.index]) {
        indexMap[item.index] = []
      }
      indexMap[item.index].push(i)
    })
    for (const key in indexMap) {
      if (indexMap[key].length > 1) {
        indexMap[key].forEach((itemIndex, i) => {
          items[itemIndex].index = `${items[itemIndex].index}-${i}`
        })
      }
    }
  }

  const setInitialActives = (items: Array<IAccordionSingleProps>): void => {
    let initiaActives = []
    items.forEach((item) => {
      if (item.initialOpen) {
        oneAtATime
          ? (initiaActives = [item.index])
          : initiaActives.push(item.index)
      }
    })
    setActives(initiaActives)
  }

  const handleClick = (
    callbackSingle: Function,
    index: number | string
  ): void => {
    if (onClick) onClick(index)
    if (callbackSingle) callbackSingle(index)
    toggleAccordion(index)
  }

  useEffect((): void => {
    checkIndexes(items)
    setInitialActives(items)
  }, [])

  return {
    actives,
    handleClick,
  }
}

export { useAccordionSingle, useAccordionMultiple }
